import {
  isAuthxAppsExplicitFailureBody,
  isAuthxAppsSuccessBody,
  normalizeAuthxResultCode,
} from "./authx-apps-response";
import { getCookie, setCrossDomainCookie } from "./cookie-utils";
import { persistAuthxSec2AndKeysFromPayload } from "./authx-sec2";
import envConfig from "../config";

export const authxAppsBase = () =>
  `https://${envConfig.apiAuthx}/api/apps`;

export const loginAuthxHeadersPreSession = (ip = "127.0.0.1") => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "olivs-api-real-ip": ip,
  "olivs-api-computer-name": "BTM-WS2019",
  "browser-agent": window.navigator.userAgent,
  "database-id": "0",
  "x-skip-auth-interceptor": "true",
});

export const authxSessionHeaders = (
  accessKey,
  refreshKey,
  ip = "127.0.0.1",
) => ({
  ...loginAuthxHeadersPreSession(ip),
  "access-key": accessKey,
  "refresh-key": refreshKey,
});

export const buildAuthxSessionGetUrl = (operation, accessKey, refreshKey) => {
  const query = new URLSearchParams({ accessKey });
  if (operation === "send-2fa-code-as-sms") {
    query.set("refreshKey", refreshKey);
  }
  return `${authxAppsBase()}/${operation}?${query.toString()}`;
};

export const normalizeAuthxSession = (raw) => {
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const xxuLike = raw.xxuID ?? raw.XxuID;
  const rcUpper = normalizeAuthxResultCode(raw);
  const http = Number(raw.httpStatusCode ?? raw.HttpStatusCode ?? Number.NaN);
  const hasKeyPair =
    Boolean(raw.accessKey || raw.AccessKey) &&
    Boolean(raw.refreshKey || raw.RefreshKey);
  let derivedSuccessYN;
  if (rcUpper === "OK") {
    derivedSuccessYN = "Y";
  } else if (hasKeyPair && (!Number.isFinite(http) || http === 200)) {
    derivedSuccessYN = "Y";
  } else if (rcUpper && rcUpper !== "OK") {
    derivedSuccessYN = "N";
  } else {
    derivedSuccessYN = raw.successYN ?? raw.SuccessYN;
  }

  return {
    accessKey: raw.accessKey ?? raw.AccessKey,
    refreshKey: raw.refreshKey ?? raw.RefreshKey,
    expiryUtc: raw.expiryUtc ?? raw.ExpiryUtc,
    sec2YN: raw.sec2YN ?? raw.Sec2YN,
    lastPwdUtc: raw.lastPwdUtc ?? raw.LastPwdUtc,
    lastMfaUtc: raw.lastMfaUtc ?? raw.LastMfaUtc,
    firstName: raw.firstName ?? raw.FirstName,
    lastName: raw.lastName ?? raw.LastName,
    globalUserId:
      raw.globalUserId ??
      raw.GlobalUserID ??
      raw.GlobalUserId ??
      raw.globalUserID ??
      xxuLike,
    successYN: derivedSuccessYN,
    xxuStatus:
      raw.xxuStatus ??
      raw.XxuStatus ??
      null,
    status:
      raw.status ??
      raw.Status ??
      raw.xxuStatus ??
      raw.XxuStatus,
    loginEmailVerifiedYN:
      raw.loginEmailVerifiedYN ??
      raw.LoginEmailVerifiedYN ??
      raw.LoginEmailVerifyYN ??
      raw.emailVerifiedYN,
    mobPhoneNoVerifiedYN:
      raw.mobPhoneNoVerifiedYN ??
      raw.MobPhoneNoVerifiedYN ??
      raw.MobPhoneNoVerifyYN ??
      raw.phoneVerifiedYN,
    mobPhoneNo:
      raw.mobPhoneNo ??
      raw.MobPhoneNo ??
      raw.mobilePhoneNo ??
      raw.MobilePhoneNo ??
      raw.xxuPhone ??
      raw.XxuPhone ??
      raw.Phone ??
      raw.phone,
    errorMessage: raw.ErrorMessage ?? raw.errorMessage,
  };
};

/**
 * Login response: xxuStatus E = email verification, P = phone verification, A = active.
 * Falls back to legacy verify flags when xxuStatus is absent.
 * @returns {"email"|"phone"|null}
 */
export function getLoginVerificationRedirect(session) {
  const s =
    session && typeof session === "object" && "accessKey" in session
      ? session
      : normalizeAuthxSession(session);
  const xxuStatus = String(
    s.xxuStatus ?? s.XxuStatus ?? s.status ?? s.Status ?? "",
  )
    .trim()
    .toUpperCase();

  if (xxuStatus === "E") {
    return "email";
  }
  if (xxuStatus === "P") {
    return "phone";
  }
  if (xxuStatus && xxuStatus !== "A") {
    if (s.loginEmailVerifiedYN === "N") {
      return "email";
    }
    if (s.mobPhoneNoVerifiedYN === "N") {
      return "phone";
    }
    return null;
  }
  if (s.loginEmailVerifiedYN === "N") {
    return "email";
  }
  if (s.mobPhoneNoVerifiedYN === "N") {
    return "phone";
  }
  return null;
}

export function isAuthxLoginSessionUsable(httpOk, sessionNorm, verificationRedirect) {
  const hasSessionKeys =
    Boolean(sessionNorm?.accessKey) && Boolean(sessionNorm?.refreshKey);
  if (!hasSessionKeys) {
    return false;
  }
  if (verificationRedirect) {
    return true;
  }
  return Boolean(httpOk) && sessionNorm.successYN !== "N";
}

export function getPendingVerificationPath() {
  const xxuStatus = String(getCookie("xxuStatus") ?? "").trim().toUpperCase();
  if (xxuStatus === "E") {
    return "/verify-email";
  }
  if (xxuStatus === "P") {
    return "/verify-phone";
  }
  if (getCookie("emailVerified") === "N") {
    return "/verify-email";
  }
  if (getCookie("phoneVerified") === "N") {
    return "/verify-phone";
  }
  return null;
}

export function hasPendingUserVerification() {
  return Boolean(getPendingVerificationPath());
}

export function persistPendingVerificationState(session, loginSecured) {
  const s = normalizeAuthxSession(session);
  const redirect = getLoginVerificationRedirect(s);
  const xxuStatus = String(s.xxuStatus ?? s.status ?? "").trim().toUpperCase();

  if (xxuStatus) {
    setCrossDomainCookie("xxuStatus", xxuStatus);
  } else if (redirect === "email") {
    setCrossDomainCookie("xxuStatus", "E");
  } else if (redirect === "phone") {
    setCrossDomainCookie("xxuStatus", "P");
  }

  if (loginSecured) {
    setCrossDomainCookie("loginSecuredHash", loginSecured);
  }

  if (redirect === "email") {
    setCrossDomainCookie("emailVerified", "N");
    setCrossDomainCookie(
      "phoneVerified",
      s.mobPhoneNoVerifiedYN === "Y" ? "Y" : "N",
    );
  } else if (redirect === "phone") {
    setCrossDomainCookie(
      "emailVerified",
      s.loginEmailVerifiedYN === "Y" ? "Y" : "N",
    );
    setCrossDomainCookie("phoneVerified", "N");
  }
}

export function clearPendingVerificationState() {
  setCrossDomainCookie("xxuStatus", "A");
  setCrossDomainCookie("emailVerified", "Y");
  setCrossDomainCookie("phoneVerified", "Y");
}

/**
 * AuthX verify-mfa often returns resultCode OK with a rotated refreshKey and accessKey null.
 * Fill missing keys from the pre-verify session.
 */
export function mergeAuthxVerifyMfaSession(prior, verificationRaw) {
  const priorAk = prior?.accessKey ?? prior?.AccessKey;
  const priorRk = prior?.refreshKey ?? prior?.RefreshKey;
  const base =
    verificationRaw && typeof verificationRaw === "object" ? verificationRaw : {};
  return normalizeAuthxSession({
    ...base,
    accessKey: base.accessKey ?? base.AccessKey ?? priorAk,
    refreshKey: base.refreshKey ?? base.RefreshKey ?? priorRk,
  });
}

/** verify-mfa success: resultCode OK / AuthX body contract, not OLIVS SuccessYN alone. */
export { shouldActivateCreatedUser, activateCreatedUser } from "./activate-created-user";

export function isVerifyMfaResponseSuccess(httpOk, verificationRaw, merged) {
  if (!httpOk || !verificationRaw || typeof verificationRaw !== "object") {
    return false;
  }
  if (Object.keys(verificationRaw).length === 0) {
    return false;
  }
  if (isAuthxAppsExplicitFailureBody(verificationRaw)) {
    return false;
  }
  if (!merged?.accessKey || !merged?.refreshKey) {
    return false;
  }
  if (merged.successYN === "N") {
    return false;
  }
  return (
    isAuthxAppsSuccessBody(verificationRaw) ||
    merged.successYN === "Y" ||
    verificationRaw.SuccessYN === "Y" ||
    verificationRaw.successYN === "Y"
  );
}

export const formatExpiryForCookie = (expiryUtc) => {
  if (!expiryUtc || typeof expiryUtc !== "string") {
    return null;
  }
  return expiryUtc.endsWith("Z") ? expiryUtc.slice(0, -1) : expiryUtc;
};

/** Zapis profilu + kluczy po verify-mfa (lub pełnym loginie). */
export const persistAuthxSessionCookies = (session, email) => {
  const s = normalizeAuthxSession(session);
  if (s.accessKey) {
    setCrossDomainCookie("accessKey", s.accessKey);
  }
  if (s.refreshKey) {
    setCrossDomainCookie("refreshKey", s.refreshKey);
  }
  const expiry = formatExpiryForCookie(s.expiryUtc);
  if (expiry) {
    setCrossDomainCookie("apiKeyExpirationUTCDate", expiry);
  }
  if (s.firstName) {
    setCrossDomainCookie("firstName", s.firstName);
  }
  if (s.lastName) {
    setCrossDomainCookie("lastName", s.lastName);
  }
  if (s.globalUserId) {
    setCrossDomainCookie("globalUserId", String(s.globalUserId));
  }
  if (email) {
    setCrossDomainCookie("loginEmail", email);
  }

  persistAuthxSec2AndKeysFromPayload(
    typeof session === "object" && session ? session : {},
  );
};
